/**
 * ModulePage — a single module's page implementing the 4-substage grammar
 * (DEV-732): Gathering Info → AI Processing → Review & Edit → Completed.
 *
 * The app controls which substage renders; the user only changes state via
 * scoped actions (answer, upload, Run, Mark Complete, Reopen, Re-run). Review
 * surfaces reuse the existing pages via deep links (scorecards / market /
 * solutions / document editor).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowRight, CheckCircle2, ListTree, Loader2, MessageSquarePlus, RotateCcw, Sparkles } from 'lucide-react'
import { Button, Card, Modal, Textarea } from '../../components/ui'
import FileDropzone from '../../components/ui/FileDropzone'
import QuestionCard from '../../components/QuestionCard'
import ProgramDocumentEditor from '../program-documents/ProgramDocumentEditor'
import ProgramSolutions from '../program-tabs/ProgramSolutions'
import ProgramMarketView from '../program-tabs/ProgramMarketView'
import ProgramCostRegister from '../program-tabs/ProgramCostRegister'
import ScorecardFullView from '../program-setup/ScorecardFullView'
import ProgramContractSelection from './ProgramContractSelection'
import { tokenManager } from '../../lib/api'
import { api } from '../../services/api'
import { useJobPoll } from '../../hooks/useJobPoll'
import { useAgent } from '../../contexts/AgentContext'
import {
  workflowsApi,
  statusLabel,
  type DownstreamChoice,
  type WorkflowStepDetail,
} from '../../services/workflows'

const API_URL = import.meta.env.VITE_API_URL || ''
const POLL_MS = 2500

const JOB_SUCCESS = ['completed', 'partial']
const JOB_FAILED = ['failed', 'canceled']

/**
 * Poll an ai.Job until it succeeds; throw on a failed/canceled terminal state
 * or timeout so the caller can surface the error (a silent success on failure
 * would leave the user staring at unchanged answers with no feedback).
 */
async function waitForJob(jobId: string): Promise<void> {
  for (let i = 0; i < 150; i++) {  // ~5 min ceiling at 2s
    const job = await api.get<{ state: string; error_message?: string }>(`/api/ai/jobs/${jobId}/`)
    if (JOB_SUCCESS.includes(job.state)) return
    if (JOB_FAILED.includes(job.state)) {
      throw new Error(job.error_message || 'Answer extraction failed')
    }
    await new Promise((r) => setTimeout(r, 2000))
  }
  throw new Error('Answer extraction is taking longer than expected — try again.')
}

/**
 * Grounds the agent on the real ProgramStep. The route supplies currentView
 * ('workflow_step') + stepKey (AgentContext); this adds the step id, live
 * substage, and name. Keyed on the context values themselves, not just the
 * step, so it re-applies whenever AgentContext's route effect replaces the
 * context wholesale (that parent effect runs after this child's on a pathname
 * change). Skips a stale `detail` from the previous step while the next loads.
 *
 * A render-nothing child so only it subscribes to AgentContext — ModulePage
 * (and its heavy review surfaces) don't re-render on every drawer toggle.
 */
function AgentStepContextSync({ detail, stepKey }: { detail: WorkflowStepDetail | null; stepKey?: string }) {
  const { pageContext, updatePageContext } = useAgent()
  const { stepId, substage, stepName } = pageContext
  useEffect(() => {
    if (!detail || detail.step_key !== stepKey) return
    if (stepId === detail.id && substage === detail.substage && stepName === detail.name) return
    updatePageContext({ stepId: detail.id, substage: detail.substage, stepName: detail.name })
  }, [detail, stepKey, stepId, substage, stepName, updatePageContext])
  return null
}

export default function ModulePage() {
  const { programId, stepKey } = useParams<{ programId: string; stepKey: string }>()
  const navigate = useNavigate()
  const [stepId, setStepId] = useState<number | null>(null)
  const [detail, setDetail] = useState<WorkflowStepDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [downstream, setDownstream] = useState<DownstreamChoice[] | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Guards the auto-run so it fires once per step (re-arms when stepId changes).
  const autoRanRef = useRef<number | null>(null)

  // Derive the "extracting" state from the job (not just local upload state) so
  // it persists if the user navigates away and back while extraction runs.
  const { activeJobs: extractJobs } = useJobPoll({
    program_step: stepId ?? undefined,
    kind: 'extract_answers',
    active: true,
    intervalMs: POLL_MS,
    enabled: stepId != null,
  })
  const extracting = uploading || extractJobs.length > 0

  // Resolve step id from step_key via the graph, then enter the step.
  useEffect(() => {
    let cancelled = false
    setDownstream(null)  // close any leftover "what's next?" modal on step change
    async function boot() {
      if (!programId || !stepKey) return
      try {
        const graph = await workflowsApi.getGraph(programId)
        const node = graph.nodes.find((n) => n.step_key === stepKey)
        if (!node) { setError('Module not found in this workflow.'); return }
        if (cancelled) return
        setStepId(node.id)
        const d = await workflowsApi.startStep(node.id)
        if (!cancelled) setDetail(d)
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load module')
      }
    }
    boot()
    return () => { cancelled = true }
  }, [programId, stepKey])

  const refresh = useCallback(async () => {
    if (stepId == null) return
    const d = await workflowsApi.getStep(stepId)
    setDetail(d)
    return d
  }, [stepId])

  // Poll while AI processing runs.
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    if (detail?.substage === 'ai_processing' && stepId != null) {
      pollRef.current = setInterval(() => { refresh() }, POLL_MS)
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [detail?.substage, stepId, refresh])

  // Auto-run: kick AI processing automatically instead of showing a Run button.
  // Fires when the gathering phase is genuinely done — either the interview's
  // required questions are all answered, or there's no interview at all (the
  // module runs on upstream context, e.g. Vendor Research). Ref-guarded so it
  // fires exactly once per step; re-arms on step change. (DEV-732.)
  // Solution Generation set to 'ask' waits for the PM's AI-vs-manual choice;
  // set to 'manual' it auto-starts the no-AI path instead.
  useEffect(() => {
    if (!detail || stepId == null || busy) return
    if (detail.substage !== 'gathering_info' || !detail.has_ai_processing) return
    if (detail.has_interview && !detail.interview_complete) return
    if (detail.generation_mode === 'ask') return
    if (autoRanRef.current === stepId) return
    autoRanRef.current = stepId
    if (detail.generation_mode === 'manual') handleStartManual()
    else handleProcess()
  }, [detail, stepId, busy])

  const handleProcess = async (feedback?: string) => {
    if (stepId == null) return
    setBusy(true); setError(null)
    try {
      setDetail(await workflowsApi.processStep(stepId, feedback))
    } catch (e: any) {
      setError(e?.message || 'Could not start processing')
    } finally { setBusy(false) }
  }

  const handleStartManual = async () => {
    if (stepId == null) return
    setBusy(true); setError(null)
    try {
      setDetail(await workflowsApi.startManual(stepId))
    } catch (e: any) {
      setError(e?.message || 'Could not start')
    } finally { setBusy(false) }
  }

  // Fast-path: fold record-answer / skip-question responses into `detail`
  // directly, avoiding the extra getStep round-trip (~500-1500ms). Both
  // endpoints now return `next_question`, `interview_progress`, and
  // `interview_complete` alongside the recorded observation.
  const handleAnswered = useCallback(async (_qid: string, _txt?: string, response?: any) => {
    if (response && typeof response === 'object' && 'next_question' in response) {
      setDetail((prev) => prev ? {
        ...prev,
        next_question: response.next_question ?? null,
        interview_progress: response.interview_progress ?? prev.interview_progress,
        interview_complete: response.interview_complete ?? prev.interview_complete,
      } : prev)
      return
    }
    // Fallback: server didn't include the fields (older backend, or an error
    // path we don't recognize) — reload the whole step.
    await refresh()
  }, [refresh])

  const handleSkipRemaining = useCallback(async () => {
    if (stepId == null) return
    setBusy(true); setError(null)
    try {
      const token = tokenManager.getAccessToken()
      const res = await fetch(
        `${API_URL}/api/workflows/steps/${stepId}/skip-remaining-and-process/`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        },
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `skip-remaining failed (${res.status})`)
      }
      setDetail(await res.json())
    } catch (e: any) {
      setError(e?.message || 'Could not skip remaining questions')
    } finally { setBusy(false) }
  }, [stepId])

  const handleConfirm = async () => {
    if (stepId == null) return
    setBusy(true)
    try {
      const res = await workflowsApi.confirmStep(stepId)
      await refresh()
      if (res.downstream.length > 1) {
        setDownstream(res.downstream)
      } else if (res.downstream.length === 1) {
        navigate(`/programs/${programId}/module/${res.downstream[0].step_key}`)
      } else {
        navigate(`/programs/${programId}`)
      }
    } catch (e: any) {
      setError(e?.message || 'Could not mark complete')
    } finally { setBusy(false) }
  }

  const handleReopen = async () => {
    if (stepId == null) return
    setBusy(true)
    try { setDetail(await workflowsApi.reopenStep(stepId)) }
    finally { setBusy(false) }
  }

  const handleUpload = async (files: File[]) => {
    if (!detail?.interview || !programId || files.length === 0) return
    setUploading(true); setError(null)
    try {
      const token = tokenManager.getAccessToken()
      for (const file of files) {
        const fd = new FormData()
        fd.append('program', String(programId))
        fd.append('title', file.name)
        fd.append('file', file)
        const uploadResp = await fetch(`${API_URL}/api/program-context-documents/`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        })
        if (!uploadResp.ok) throw new Error('Upload failed')
        const doc = await uploadResp.json()
        // Extraction runs async (10–30s): kick it, then poll the job to done.
        const { job_id } = await api.post<{ job_id: string }>(
          `/api/documents/interviews/${detail.interview}/extract-answers/`,
          { document_id: doc.id },
        )
        await waitForJob(job_id)
      }
      await refresh()
    } catch (e: any) {
      setError(e?.message || 'Extraction failed')
    } finally { setUploading(false) }
  }

  if (error && !detail) return <div className="p-8 text-destructive">{error}</div>
  if (!detail) {
    return (
      <div className="flex items-center justify-center h-96 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading module…
      </div>
    )
  }

  // Modules that keep their review surface mounted while AI processing runs,
  // instead of handing the page over to the centred progress card. Declared by
  // the adapter (`ModuleAdapter.review_during_processing`) so the page keeps a
  // single code path rather than special-casing a step key.
  const reviewDuringProcessing =
    detail.substage === 'ai_processing' && !!detail.review_during_processing

  // Wide inline review surfaces (solutions grid / market view / scorecard) get more room.
  const wideSurface =
    (detail.substage === 'review_edit' ||
      detail.substage === 'completed' ||
      reviewDuringProcessing) &&
    ['solutions', 'marketview', 'scorecard', 'costing', 'contracts'].includes(detail.review_surface)

  return (
    <div className={`p-6 mx-auto ${wideSurface ? 'max-w-6xl' : 'max-w-3xl'}`}>
      <AgentStepContextSync detail={detail} stepKey={stepKey} />
      <button onClick={() => navigate(`/programs/${programId}`)}
        className="text-sm text-muted-foreground hover:text-foreground mb-2">← Back to workflow</button>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-foreground">{detail.name}</h1>
        <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
          {statusLabel(detail.status)}
        </span>
      </div>
      <div className="mb-6">
        <SubstageStrip substage={detail.substage} />
        {detail.description && (
          <p className="text-sm text-muted-foreground mt-3 max-w-2xl">{detail.description}</p>
        )}
      </div>

      {detail.needs_attention_reason && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {detail.needs_attention_reason}
        </div>
      )}
      {error && <div className="mb-4 text-sm text-destructive">{error}</div>}

      {detail.substage === 'gathering_info' && (
        <GatheringStage
          detail={detail}
          uploading={extracting}
          busy={busy}
          onAnswered={handleAnswered}
          onUpload={handleUpload}
          onProcess={handleProcess}
          onStartManual={handleStartManual}
          onSkipRemaining={handleSkipRemaining}
        />
      )}

      {detail.substage === 'ai_processing' && !reviewDuringProcessing && (
        <ProcessingStage detail={detail} />
      )}

      {reviewDuringProcessing && (
        <div className="space-y-4">
          <ProcessingBanner detail={detail} />
          <ReviewSurface detail={detail} programId={programId!} onDetail={setDetail} />
        </div>
      )}

      {detail.substage === 'review_edit' && (
        <ReviewStage
          detail={detail}
          programId={programId!}
          busy={busy}
          onConfirm={handleConfirm}
          onReRun={handleProcess}
          onStartManual={handleStartManual}
          onDetail={setDetail}
          onRefresh={refresh}
        />
      )}

      {detail.substage === 'completed' && (
        <CompletedStage detail={detail} programId={programId!} busy={busy} onReopen={handleReopen} />
      )}

      {downstream && (
        <Modal isOpen onClose={() => setDownstream(null)} title="What's next?">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">This module unlocks more than one next step. Where to?</p>
            {downstream.map((d) => (
              <Button key={d.step_id} variant="outline" className="w-full justify-between"
                onClick={() => { setDownstream(null); navigate(`/programs/${programId}/module/${d.step_key}`) }}>
                {d.name} <ArrowRight className="w-4 h-4" />
              </Button>
            ))}
            <Button variant="ghost" className="w-full" onClick={() => { setDownstream(null); navigate(`/programs/${programId}`) }}>
              Back to workflow
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ── substage strip ──────────────────────────────────────────────────────────

const SUBSTAGES: { key: string; label: string }[] = [
  { key: 'gathering_info', label: 'Gather Info' },
  { key: 'ai_processing', label: 'AI Processing' },
  { key: 'review_edit', label: 'Review & Edit' },
  { key: 'completed', label: 'Complete' },
]

function SubstageStrip({ substage }: { substage: string }) {
  const idx = SUBSTAGES.findIndex((s) => s.key === substage)
  return (
    <div className="flex items-center gap-2 text-xs">
      {SUBSTAGES.map((s, i) => (
        <div key={s.key} className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full ${
            i < idx ? 'bg-foreground text-background'
            : i === idx ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground'}`}>{s.label}</span>
          {i < SUBSTAGES.length - 1 && <span className="text-muted-foreground">›</span>}
        </div>
      ))}
    </div>
  )
}

// ── stages ────────────────────────────────────────────────────────────────

function GatheringStage({ detail, uploading, busy, onAnswered, onUpload, onProcess, onStartManual, onSkipRemaining }: {
  detail: WorkflowStepDetail; uploading: boolean; busy: boolean
  onAnswered: (questionId: string, answerText?: string, response?: any) => void
  onUpload: (f: File[]) => void
  onProcess: () => void
  onStartManual: () => void
  onSkipRemaining: () => void
}) {
  // While a document is being read, take over the stage with the AI-processing
  // animation so it's obvious work is happening (DEV-732 edit #1).
  if (uploading) {
    return (
      <Card className="p-8 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-3" />
        <div className="font-medium text-foreground">Reading your document…</div>
        <div className="text-sm text-muted-foreground mt-1">
          Extracting answers to the interview questions — this takes a few seconds.
        </div>
        <div className="mt-4 h-1.5 w-full max-w-sm mx-auto rounded-full bg-muted overflow-hidden">
          <div className="h-full w-1/3 bg-primary animate-pulse" />
        </div>
      </Card>
    )
  }

  // Solution Generation asks up front: generate families with AI, or pick
  // them by hand from the existing tech tree (no AI generation).
  if (detail.generation_mode === 'ask') {
    return <GenerationModeChoice busy={busy} onAi={onProcess} onManual={onStartManual} />
  }

  const q = detail.next_question
  const complete = detail.interview_complete
  const hasInterview = detail.has_interview

  // Interview satisfied → the parent auto-kicks AI processing; show a brief
  // "starting" state instead of a manual Run button (DEV-732). Gated on `busy`
  // so a failed auto-run falls back to a manual Run rather than a stuck spinner.
  if (hasInterview && complete && detail.has_ai_processing) {
    if (busy) {
      return (
        <Card className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-3" />
          <div className="font-medium text-foreground">All set — starting analysis…</div>
          <div className="text-sm text-muted-foreground mt-1">
            Every required question is answered. Running {detail.name}.
          </div>
        </Card>
      )
    }
    return (
      <Card className="p-6 text-center space-y-3">
        <div className="text-sm text-muted-foreground">All required questions are answered.</div>
        <Button onClick={() => onProcess()}>
          <Sparkles className="w-4 h-4 mr-2" /> Run {detail.name}
        </Button>
      </Card>
    )
  }

  // Non-interview processing module: nothing to gather. Auto-run kicks
  // immediately (ModulePage useEffect), so the normal path shows the
  // "starting" card and then flips to ai_processing. The manual Run button
  // is the fallback for when auto-run failed and left the step here.
  if (!hasInterview && detail.has_ai_processing) {
    if (busy) {
      return (
        <Card className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-3" />
          <div className="font-medium text-foreground">Starting {detail.name}…</div>
          <div className="text-sm text-muted-foreground mt-1">
            Running on the context already gathered upstream.
          </div>
        </Card>
      )
    }
    return (
      <Card className="p-6 text-center space-y-3">
        <div className="text-sm text-muted-foreground">
          This step runs on the context already gathered upstream.
        </div>
        <Button onClick={() => onProcess()}>
          <Sparkles className="w-4 h-4 mr-2" /> Run {detail.name}
        </Button>
      </Card>
    )
  }

  // Interview in progress: two ways in — answer questions OR upload a document,
  // with "or" between the boxes. Progress is answered-based (not the current
  // question's position) so a doc that filled most gaps reads "9 of 10
  // answered · 1 to go" instead of a confusing "10 of 10" (DEV-732).
  const progress = detail.interview_progress
  return (
    <div className="space-y-3">
      {hasInterview && (
        <div>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <div className="text-sm font-medium text-foreground">Answer questions</div>
            <div className="flex items-center gap-3">
              {progress && progress.total > 0 && (
                <span className="text-xs text-muted-foreground">
                  {progress.answered} of {progress.total} answered
                  {progress.answered > 0 && progress.remaining.length > 0
                    ? ` · ${progress.remaining.length} to go`
                    : ''}
                </span>
              )}
              {progress && progress.remaining.length > 0 && detail.has_ai_processing && (
                <button
                  type="button"
                  onClick={onSkipRemaining}
                  disabled={busy}
                  className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Skip all remaining questions and start AI processing"
                >
                  Skip remaining →
                </button>
              )}
            </div>
          </div>
          {q ? (
            // key forces a fresh QuestionCard per question — without it the same
            // instance is reused and stays stuck on its "answer recorded" state
            // instead of advancing to the next question.
            <QuestionCard
              key={q.question_id}
              question={q}
              onAnswered={onAnswered}
              onSkip={(qid, response) => onAnswered(qid, '(skipped)', response)}
              hideProgress
            />
          ) : detail.interview_error ? (
            <Card role="alert" className="p-4 text-sm text-destructive">{detail.interview_error}</Card>
          ) : (
            <Card className="p-4 text-sm text-muted-foreground">Loading questions…</Card>
          )}
        </div>
      )}

      {hasInterview && detail.interview && (
        <>
          <div className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
          </div>
          <div>
            <div className="text-sm font-medium text-foreground mb-1">Upload a document</div>
            <p className="text-xs text-muted-foreground mb-3">
              We'll extract answers and only ask about the gaps.
            </p>
            <FileDropzone onFilesSelected={onUpload} />
          </div>
        </>
      )}
    </div>
  )
}

function ProcessingStage({ detail }: { detail: WorkflowStepDetail }) {
  const p = detail.processing
  return (
    <Card className="p-8 text-center">
      <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-3" />
      <div className="font-medium text-foreground">Working on it…</div>
      <div className="text-sm text-muted-foreground mt-1">{p?.label || 'Running AI analysis'}</div>
      <div className="mt-4 h-1.5 w-full max-w-sm mx-auto rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary transition-all" style={{ width: `${p?.progress ?? 5}%` }} />
      </div>
    </Card>
  )
}

/**
 * The processing state for a module that keeps its review surface mounted: one
 * line above the surface instead of a takeover card, so the user can still see
 * (and work with) what has landed so far while the run finishes.
 */
function ProcessingBanner({ detail }: { detail: WorkflowStepDetail }) {
  const p = detail.processing
  return (
    <div role="status"
      className="flex items-center gap-3 rounded-md border border-border bg-muted/40 px-3 py-2">
      <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
      <span className="text-sm text-foreground">{p?.label || 'Running AI analysis'}</span>
      <div className="ml-auto h-1.5 w-32 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary transition-all" style={{ width: `${p?.progress ?? 5}%` }} />
      </div>
    </div>
  )
}

function reviewSurfaceLink(detail: WorkflowStepDetail, programId: string): { href: string; label: string } {
  const ctx = detail.review_context || {}
  switch (detail.review_surface) {
    case 'scorecard':
      return { href: `/programs/${programId}/setup/scorecards`, label: 'Open the scorecard editor' }
    case 'document':
      return {
        href: ctx.program_document_id
          ? `/programs/${programId}/documents/${ctx.program_document_id}`
          : `/programs/${programId}/setup/documents`,
        label: 'Open the document editor',
      }
    case 'marketview':
      return { href: `/programs/${programId}/market`, label: 'Open Market View' }
    case 'solutions':
      return { href: `/programs/${programId}/solutions`, label: 'Open Solutions' }
    case 'costing':
      return { href: `/programs/${programId}/cost-register`, label: 'Open Cost Register' }
    default:
      return { href: `/programs/${programId}`, label: 'Open' }
  }
}

/**
 * What the Vendor Research step is for and what the user has to do in it.
 * Starring is the action the downstream Market Research Report reads, and
 * nothing on the Market View surface itself says so — without this, PMs
 * completed the step having starred nothing and got a supplier table of
 * "[To be determined]".
 */
function VendorResearchInstructions() {
  return (
    <Card className="p-4 space-y-2">
      <div className="text-sm font-medium text-foreground">Shortlist your candidates</div>
      <p className="text-sm text-muted-foreground">
        See relevant products scored against your scorecards. Shortlist items for further
        analysis by clicking the star icon on one or more tiles.
      </p>
    </Card>
  )
}

function ReviewSurface({ detail, programId, onDetail, onShortlistChange }: {
  detail: WorkflowStepDetail; programId: string; onDetail?: (d: WorkflowStepDetail) => void
  /** Re-read the step after a shortlist change, so the confirm guard the page
   *  shows tracks what the user just starred. */
  onShortlistChange?: () => void
}) {
  const navigate = useNavigate()
  const ctx = detail.review_context || {}
  // Contract Selection: ranked types + valid vehicles, answered and picked inline.
  if (detail.review_surface === 'contracts') {
    return <ProgramContractSelection detail={detail} onDetail={onDetail} />
  }
  // Document review is edited inline in the module page (no deep link).
  if (detail.review_surface === 'document' && ctx.program_document_id) {
    return (
      <Card className="p-4">
        <ProgramDocumentEditor embedded programId={programId} programDocumentId={ctx.program_document_id} />
      </Card>
    )
  }
  // Solution Families reviewed inline (accept/reject flows through to Vendor Research).
  if (detail.review_surface === 'solutions') {
    return (
      <Card className="p-4">
        <ProgramSolutions embedded programId={programId} />
      </Card>
    )
  }
  // Market View reviewed inline (browse scored candidates + shortlist).
  if (detail.review_surface === 'marketview') {
    return (
      <div className="space-y-3">
        {detail.substage === 'review_edit' && <VendorResearchInstructions />}
        <ProgramMarketView embedded programId={programId} onShortlistChange={onShortlistChange} />
      </div>
    )
  }
  // The Cost Register reviewed inline (approve / exclude lines for the IGCE).
  if (detail.review_surface === 'costing') {
    return <ProgramCostRegister embedded programId={programId} />
  }
  // Scorecard (basic requirements / technical) reviewed + edited inline, locked
  // to the module's scorecard type.
  if (detail.review_surface === 'scorecard') {
    return (
      <Card className="p-4">
        <ScorecardFullView embedded programId={programId} focusType={ctx.scorecard_type as any} />
      </Card>
    )
  }
  const link = reviewSurfaceLink(detail, programId)
  return (
    <Card className="p-4">
      <div className="text-sm text-foreground mb-3">
        The AI output is ready. Review and edit it, then mark this module complete.
      </div>
      <Button variant="outline" onClick={() => navigate(link.href)}>
        {link.label} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </Card>
  )
}

function GenerationModeChoice({ busy, onAi, onManual }: {
  busy: boolean; onAi: () => void; onManual: () => void
}) {
  const options = [
    {
      key: 'ai',
      icon: Sparkles,
      title: 'Generate with AI',
      body: 'Propose solution families from your Capability Need Statement. You review and accept them once it finishes.',
      onClick: onAi,
    },
    {
      key: 'manual',
      icon: ListTree,
      title: 'Pick from the tech tree',
      body: 'Search or browse the existing tech tree and add the families yourself. No AI generation.',
      onClick: onManual,
    },
  ]
  return (
    <div className="space-y-3">
      <div className="text-sm text-foreground">How do you want to build your solution families?</div>
      <div className="grid gap-3 sm:grid-cols-2">
        {options.map(({ key, icon: Icon, title, body, onClick }) => (
          <button
            key={key}
            type="button"
            disabled={busy}
            onClick={onClick}
            className="flex flex-col gap-2 rounded-lg border border-border bg-card p-5 text-left transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          >
            <Icon className="size-5 text-foreground" />
            <span className="font-medium text-foreground">{title}</span>
            <span className="text-sm text-muted-foreground">{body}</span>
          </button>
        ))}
      </div>
      {busy && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Starting…
        </div>
      )}
    </div>
  )
}

function ReviewStage({ detail, programId, busy, onConfirm, onReRun, onStartManual, onDetail, onRefresh }: {
  detail: WorkflowStepDetail; programId: string; busy: boolean
  onConfirm: () => void; onReRun: (feedback?: string) => void
  onStartManual: () => void
  onDetail?: (d: WorkflowStepDetail) => void
  onRefresh?: () => void
}) {
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedback, setFeedback] = useState('')
  // Solution Generation: a hand-picked taxonomy has nothing to re-run; offer the
  // other path instead (each replaces the current set of families).
  const manualTaxonomy = detail.review_context?.taxonomy_mode === 'manual'
  const canSwitchToManual = detail.generation_mode === 'ask' && !manualTaxonomy
  // Vendor Research re-runs per solution family from Market View, so it opts
  // out of the step-level controls (`ModuleAdapter.supports_step_rerun`).
  // `supports_step_rerun` defaults to true server-side; `!== false` keeps an
  // older payload that predates the field behaving as it did before.
  const stepRerun =
    detail.has_ai_processing && !manualTaxonomy && detail.supports_step_rerun !== false
  // The module's own completion precondition, as the server reports it. Only a
  // hint — `confirm/` enforces it and returns the same sentences — so the page
  // can stay honest if this payload is ever missing (nothing blocks, the
  // server still refuses and `onConfirm` surfaces its message).
  const confirmBlockers = detail.confirm_blockers ?? []
  const switchTo = (to: 'ai' | 'manual') => {
    const ok = window.confirm(
      to === 'ai'
        ? 'Generate solution families with AI?\n\nThis replaces the families you picked by hand.'
        : 'Pick solution families by hand instead?\n\nThis starts an empty set and replaces the AI-generated families.',
    )
    if (!ok) return
    if (to === 'ai') onReRun()
    else onStartManual()
  }
  return (
    <div className="space-y-4">
      {/* Actions sit above the AI output so the primary next-step is reachable
          without scrolling past the review surface (DEV-732). */}
      <div className="flex items-center gap-2">
        <Button
          onClick={onConfirm}
          disabled={busy || confirmBlockers.length > 0}
          title={confirmBlockers.join(' ') || undefined}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          Mark as Complete
        </Button>
        {manualTaxonomy && (
          <Button variant="ghost" onClick={() => switchTo('ai')} disabled={busy}>
            <Sparkles className="w-4 h-4 mr-2" /> Generate with AI instead
          </Button>
        )}
        {stepRerun && (
          <>
            <Button variant="ghost" onClick={() => onReRun()} disabled={busy}>
              <RotateCcw className="w-4 h-4 mr-2" /> Re-run
            </Button>
            <Button variant="ghost" onClick={() => setShowFeedback((s) => !s)} disabled={busy}>
              <MessageSquarePlus className="w-4 h-4 mr-2" /> Re-run with feedback
            </Button>
          </>
        )}
        {canSwitchToManual && (
          <Button variant="ghost" onClick={() => switchTo('manual')} disabled={busy}>
            <ListTree className="w-4 h-4 mr-2" /> Pick by hand instead
          </Button>
        )}
      </div>
      {stepRerun && showFeedback && (
        <Card className="p-4 space-y-3">
          <div className="text-sm font-medium text-foreground">Guidance for the re-run</div>
          <p className="text-xs text-muted-foreground">
            Tell the AI what to change on this pass — e.g. “focus on lower-cost families” or
            “emphasize domestic manufacturers.” Your note is folded into the prompt.
          </p>
          <Textarea
            rows={3}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="What should change on this pass?"
          />
          <div className="flex items-center gap-2">
            <Button
              onClick={() => { onReRun(feedback.trim()); setShowFeedback(false) }}
              disabled={busy || !feedback.trim()}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Re-run with this feedback
            </Button>
            <Button variant="ghost" onClick={() => { setShowFeedback(false); setFeedback('') }} disabled={busy}>
              Cancel
            </Button>
          </div>
        </Card>
      )}
      {confirmBlockers.length > 0 && (
        <div role="status"
          className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-foreground">
          {confirmBlockers.join(' ')}
        </div>
      )}
      <ReviewSurface
        detail={detail}
        programId={programId}
        onDetail={onDetail}
        onShortlistChange={onRefresh}
      />
    </div>
  )
}

function CompletedStage({ detail, programId, busy, onReopen }: {
  detail: WorkflowStepDetail; programId: string; busy: boolean; onReopen: () => void
}) {
  // No redundant "Completed (v1)" summary card — status already lives in the
  // header badge + substage strip, and the artifact itself renders below
  // (DEV-732). Just show the output surface + Reopen.
  return (
    <div className="space-y-4">
      <ReviewSurface detail={detail} programId={programId} />
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onReopen} disabled={busy}>
          <RotateCcw className="w-4 h-4 mr-2" /> Reopen
        </Button>
      </div>
    </div>
  )
}
